import React, { type FC, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { SearchIcon } from 'lucide-react';

import { useEstateOptions } from '@/api/estate-options';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import { Input } from '@/components/ui/input';
import { Select, type SelectOption } from '@/components/ui/select';
import { PROPERTY_LEVELS } from '@/constants/property-level';
import { FilterButton } from '@/features/estates/components/filter-button';
import { ViewSwitch } from '@/features/estates/components/view-switch';
import { useProjects } from '@/features/projects/api/projects';
import { useDebouncedCallback } from '@/hooks/use-debounced-callback';
import { cn } from '@/lib/utils';
import { parseObjectToSearchParams } from '@/utils/parseSearchParams';

import type { EstatesFiltersSchemaType } from '../schemas';

const additionalFeatures: (keyof Pick<
  EstatesFiltersSchemaType,
  'loggia' | 'balcony' | 'garden' | 'parkingSpace' | 'basement' | 'elevator'
>)[] = ['loggia', 'balcony', 'garden', 'parkingSpace', 'basement', 'elevator'];

const roomsOptions = [
  { id: '1', title: '1+' },
  { id: '2', title: '2+' },
  { id: '3', title: '3+' },
  { id: '4', title: '4+' },
  { id: '5', title: '5+' },
];

export interface ToolbarProps {
  url:
    | '/_authenticated/_sidebar-layout/projects/$projectId/_dashboard-layout/objects'
    | '/_authenticated/_sidebar-layout/estates/'
    | '/_authenticated/_sidebar-layout/estates/me'
    | '/_authenticated/_sidebar-layout/tenant/$tenantId/estates';

  showViewSwitch?: boolean;
  creatorList?: SelectOption[];
  rightButtonAction?: React.ReactNode;
}

export const Toolbar: FC<ToolbarProps> = ({
  url,
  creatorList,
  showViewSwitch = true,
  rightButtonAction,
}) => {
  const route = getRouteApi(url);
  const search = route.useSearch();
  const { enableFilters, filters, filterOwn, view } = useMemo(() => {
    const { filters, view } = search;
    const enableFilters = 'enableFilters' in search ? search.enableFilters : undefined;
    const filterOwn = 'filterOwn' in search ? search.filterOwn : undefined;
    return {
      view,
      filters,
      enableFilters,
      filterOwn,
    };
  }, [search]);
  const navigate = route.useNavigate();
  const location = route.useMatch();

  const [t] = useTranslation();

  const [isCreatedFromValid, setIsCreatedFromValid] = useState<boolean>(true);
  const [isCreatedToValid, setIsCreatedToValid] = useState<boolean>(true);
  const [isUpdatedFromValid, setIsUpdatedFromValid] = useState<boolean>(true);
  const [isUpdatedToValid, setIsUpdatedToValid] = useState<boolean>(true);

  const { data } = useEstateOptions();
  const { data: projects } = useProjects();

  const projectOptions = useMemo<SelectOption[]>(
    () =>
      projects?.map((project) => ({
        title: project.name || `Project_${project.id}`,
        id: project.id,
      })) || [],
    [projects]
  );

  const methods = useForm<EstatesFiltersSchemaType>({
    defaultValues: {
      rooms: '',
      rentalPrices: {
        min: '',
        max: '',
      },
      buyingPrices: {
        min: '',
        max: '',
      },
      createdAt: {
        min: '',
        max: '',
      },
      updatedAt: {
        min: '',
        max: '',
      },
      heatingType: '',
      flooring: '',
      glazing: '',
      loggia: false,
      balcony: false,
      garden: false,
      parkingSpace: false,
      basement: false,
      elevator: false,
      minLivingArea: '',
      usageType: '',
      marketType: '',
      searchString: '',
      propertyType: '',
      propertyLevel: '',
      projectId: '',
      creatorId: filterOwn && creatorList?.length ? creatorList[0].id : undefined,
    },
  });

  const { reset, watch } = methods;

  const updateSearchParams = (params: Record<string, string | (string | undefined)[]>) => {
    const filters = parseObjectToSearchParams<Partial<EstatesFiltersSchemaType>>(params);
    navigate({
      search: (prev) => ({ ...prev, filters, view: prev.view || 'grid' }),
    });
  };

  const debouncedUpdateSearchParamFn = useDebouncedCallback(updateSearchParams, 1000);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored
  React.useEffect(() => {
    reset((prev) => ({ ...prev, ...filters }));
    const sub = watch((data, { type }) => {
      if (type === 'change') {
        debouncedUpdateSearchParamFn(data);
      }
    });

    return () => sub.unsubscribe();
  }, []);

  return (
    <FormProvider {...methods}>
      <Card className={cn('rounded-[12px] p-3', view === 'map' && 'relative overflow-visible')}>
        <div className="grid grid-cols-[1fr_max-content_max-content_max-content_max-content] gap-1">
          <Controller
            {...methods}
            name="searchString"
            render={({ field }) => (
              <Input
                size="lg"
                placeholder={t('search')}
                startIcon={<SearchIcon className="text-primary-light" />}
                {...field}
              />
            )}
          />
          <Controller
            {...methods}
            name="minLivingArea"
            render={({ field }) => (
              <Input
                size="lg"
                type="number"
                className="pr-[30px] max-xl:hidden"
                placeholder={t('living area from')}
                endIcon={
                  <div className="absolute right-[16px] top-[17px] block text-md text-primary-light max-xl:hidden">
                    m<sup>2</sup>
                  </div>
                }
                {...field}
              />
            )}
          />
          <Controller
            {...methods}
            name="rooms"
            render={({ field }) => (
              <Select
                placeholder={t('numberOfRooms')}
                size="lg"
                className="block min-w-36 self-end max-xl:hidden"
                options={roomsOptions}
                {...field}
              />
            )}
          />
          <FilterButton url={url} />
          {showViewSwitch ? <ViewSwitch url={url} from={location.pathname} /> : null}
          {rightButtonAction}
        </div>
        <div
          className={cn(
            'grid',
            view !== 'map'
              ? 'transition-grid-template-rows grid duration-200 motion-reduce:transition-none'
              : 'absolute left-0 z-10 w-full rounded-bl-md rounded-br-md bg-white shadow-md',
            enableFilters ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          )}
        >
          <div
            className={cn(
              'overflow-hidden',
              enableFilters && view === 'map' ? 'overflow-visible' : ''
            )}
          >
            <div className="mb-[20px] mt-[25px] border-t border-t-background-lighter px-[30px] pt-[20px]">
              <fieldset className="mb-[20px] grid grid-cols-2 gap-6 max-md:grid-cols-2 max-sm:grid-cols-1 max-sm:gap-2">
                <Controller
                  {...methods}
                  name="projectId"
                  render={({ field }) => (
                    <Select
                      label={t('project')}
                      size="lg"
                      className="self-end"
                      options={projectOptions}
                      {...field}
                    />
                  )}
                />
              </fieldset>
              <fieldset className="mb-[20px] grid grid-cols-4 gap-6 max-md:grid-cols-2 max-sm:grid-cols-1 max-sm:gap-2">
                <Controller
                  {...methods}
                  name="buyingPrices.min"
                  render={({ field }) => (
                    <Input type="number" label={t('buying-price-from')} size="lg" {...field} />
                  )}
                />
                <Controller
                  {...methods}
                  name="buyingPrices.max"
                  render={({ field }) => (
                    <Input type="number" label={t('buying-price-to')} size="lg" {...field} />
                  )}
                />
                <Controller
                  {...methods}
                  name="rentalPrices.min"
                  render={({ field }) => (
                    <Input type="number" label={t('rental-price-from')} size="lg" {...field} />
                  )}
                />
                <Controller
                  {...methods}
                  name="rentalPrices.max"
                  render={({ field }) => (
                    <Input type="number" label={t('rental-price-to')} size="lg" {...field} />
                  )}
                />
              </fieldset>
              <fieldset className="mb-[20px] grid grid-cols-4 gap-6 max-md:grid-cols-2 max-sm:grid-cols-1 max-sm:gap-2">
                <Controller
                  {...methods}
                  name="marketType"
                  render={({ field }) => (
                    <Select
                      label={t('marketingType')}
                      size="lg"
                      className="self-end"
                      options={data?.marketingType || []}
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="usageType"
                  render={({ field }) => (
                    <Select
                      label={t('usage')}
                      size="lg"
                      className="self-end"
                      options={data?.usage || []}
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="propertyType"
                  render={({ field }) => (
                    <Select
                      label={t('propertyType')}
                      size="lg"
                      className="self-end"
                      options={data?.propertyType || []}
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="heatingType"
                  render={({ field }) => (
                    <Select
                      label={t('heatingType')}
                      size="lg"
                      className="self-end"
                      options={data?.heatingType || []}
                      {...field}
                    />
                  )}
                />
              </fieldset>
              <fieldset
                className={cn('mb-[20px] grid grid-cols-3 gap-6', creatorList && 'grid-cols-4')}
              >
                {creatorList ? (
                  <Controller
                    {...methods}
                    name="creatorId"
                    render={({ field }) => (
                      <Select
                        label={t('author')}
                        size="lg"
                        className={cn('self-end', filterOwn && '[&_svg]:hidden')}
                        options={creatorList}
                        multiple
                        {...field}
                        onChange={(ev) => {
                          if (!filterOwn) {
                            field.onChange(ev);
                          }
                        }}
                        value={
                          field.value
                            ? Array.isArray(field.value)
                              ? field.value
                              : [field.value]
                            : []
                        }
                      />
                    )}
                  />
                ) : null}
                <Controller
                  {...methods}
                  name="flooring"
                  render={({ field }) => (
                    <Select
                      label={t('flooring')}
                      size="lg"
                      className="self-end"
                      options={data?.flooring || []}
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="glazing"
                  render={({ field }) => (
                    <Select
                      label={t('glazing')}
                      size="lg"
                      className="self-end"
                      options={data?.glazing || []}
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="propertyLevel"
                  render={({ field }) => (
                    <Select
                      label={t('property-type')}
                      size="lg"
                      className="self-end"
                      options={[
                        {
                          title: t('project'),
                          id: PROPERTY_LEVELS.PROJECT,
                        },
                        {
                          title: t('object'),
                          id: PROPERTY_LEVELS.OBJECT,
                        },
                      ]}
                      {...field}
                    />
                  )}
                />
              </fieldset>
              <fieldset className="mb-[20px] grid grid-cols-1 gap-6 xl:hidden">
                <Controller
                  {...methods}
                  name="minLivingArea"
                  render={({ field }) => (
                    <Input
                      size="lg"
                      type="number"
                      className="block pr-[30px] xl:hidden"
                      placeholder={t('living area from')}
                      endIcon={
                        <div className="absolute right-[16px] top-[17px] block text-md text-primary-light xl:hidden">
                          m<sup>2</sup>
                        </div>
                      }
                      {...field}
                    />
                  )}
                />
                <Controller
                  {...methods}
                  name="rooms"
                  render={({ field }) => (
                    <Select
                      placeholder={t('numberOfRooms')}
                      size="lg"
                      className="block min-w-36 self-end xl:hidden"
                      options={roomsOptions}
                      {...field}
                    />
                  )}
                />
              </fieldset>
              <fieldset className="mb-[20px] grid grid-cols-4 gap-6">
                <Controller
                  {...methods}
                  name="createdAt.min"
                  render={({ field }) => (
                    <div className="relative flex w-full flex-col gap-[10px] overflow-hidden">
                      <span
                        className={cn(
                          'select-none text-xs',
                          isCreatedFromValid ? 'text-text-secondary' : 'text-red-500'
                        )}
                      >
                        {t('createdFrom')}
                      </span>
                      <DatePicker
                        {...field}
                        setIsValid={setIsCreatedFromValid}
                        isValid={isCreatedFromValid}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        className={cn(
                          'flex h-[57px] !w-full !justify-center rounded-xs border bg-primary-lighter px-6 shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [&_input]:w-full [&_input]:text-md [&_input]:text-[--input-color] [&_input]:outline-none',
                          isCreatedFromValid ? 'border-[--input-color]' : 'border-red-500'
                        )}
                      />
                    </div>
                  )}
                />
                <Controller
                  {...methods}
                  name="createdAt.max"
                  render={({ field }) => (
                    <div className="relative flex w-full flex-col gap-[10px] overflow-hidden">
                      <span
                        className={cn(
                          'select-none text-xs',
                          isCreatedToValid ? 'text-text-secondary' : 'text-red-500'
                        )}
                      >
                        {t('createdTo')}
                      </span>
                      <DatePicker
                        {...field}
                        setIsValid={setIsCreatedToValid}
                        isValid={isCreatedToValid}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        className={cn(
                          'flex h-[57px] !w-full !justify-center rounded-xs border bg-primary-lighter px-6 shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [&_input]:w-full [&_input]:text-md [&_input]:text-[--input-color] [&_input]:outline-none',
                          isCreatedToValid ? 'border-[--input-color]' : 'border-red-500'
                        )}
                      />
                    </div>
                  )}
                />
                <Controller
                  {...methods}
                  name="updatedAt.min"
                  render={({ field }) => (
                    <div className="relative flex w-full flex-col gap-[10px] overflow-hidden">
                      <span
                        className={cn(
                          'select-none text-xs',
                          isUpdatedFromValid ? 'text-text-secondary' : 'text-red-500'
                        )}
                      >
                        {t('updatedFrom')}
                      </span>
                      <DatePicker
                        {...field}
                        setIsValid={setIsUpdatedFromValid}
                        isValid={isUpdatedFromValid}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        className={cn(
                          'flex h-[57px] !w-full !justify-center rounded-xs border bg-primary-lighter px-6 shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [&_input]:w-full [&_input]:text-md [&_input]:text-[--input-color] [&_input]:outline-none',
                          isUpdatedFromValid ? 'border-[--input-color]' : 'border-red-500'
                        )}
                      />
                    </div>
                  )}
                />
                <Controller
                  {...methods}
                  name="updatedAt.max"
                  render={({ field }) => (
                    <div className="relative flex w-full flex-col gap-[10px] overflow-hidden">
                      <span
                        className={cn(
                          'select-none text-xs',
                          isUpdatedToValid ? 'text-text-secondary' : 'text-red-500'
                        )}
                      >
                        {t('updatedTo')}
                      </span>
                      <DatePicker
                        {...field}
                        setIsValid={setIsUpdatedToValid}
                        isValid={isUpdatedToValid}
                        value={field.value ? new Date(field.value) : undefined}
                        onChange={(date) => {
                          field.onChange(date);
                        }}
                        className={cn(
                          'flex h-[57px] !w-full !justify-center rounded-xs border bg-primary-lighter px-6 shadow-sm transition-colors [--input-color:hsla(var(--primary-color-light))] [&_input]:w-full [&_input]:text-md [&_input]:text-[--input-color] [&_input]:outline-none',
                          isUpdatedToValid ? 'border-[--input-color]' : 'border-red-500'
                        )}
                      />
                    </div>
                  )}
                />
              </fieldset>
              <fieldset className="mb-[20px] grid grid-cols-4 gap-6 max-md:grid-cols-2 max-sm:grid-cols-1 max-sm:gap-2">
                {additionalFeatures.map((fieldName) => (
                  <Controller
                    key={fieldName}
                    name={fieldName}
                    render={({ field: { value, onChange, ...field }, formState: { errors } }) => (
                      <Checkbox
                        checked={value}
                        label={t(fieldName)}
                        error={Boolean(errors[fieldName]?.message)}
                        onClick={() => onChange(!value)}
                        {...field}
                      />
                    )}
                    {...methods}
                  />
                ))}
              </fieldset>
            </div>
          </div>
        </div>
      </Card>
    </FormProvider>
  );
};
